import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { stopScroll, startScroll } from "../../lib/lenis";
import { M } from "../../data/site";
import { QuickJoin } from "./QuickJoin";

const SRC = "/media/explainer.mp4";

export const DemoVideoModal = ({ open, onClose }) => {
  const vref = useRef(null);
  const [ended, setEnded] = useState(false);

  const close = () => {
    const v = vref.current;
    if (v) { v.pause(); v.currentTime = 0; }
    setEnded(false);
    onClose();
  };

  const again = () => {
    const v = vref.current;
    setEnded(false);
    if (v) { v.currentTime = 0; v.play()?.catch(() => {}); }
  };

  useEffect(() => {
    if (!open) return undefined;
    stopScroll();
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); startScroll(); };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const v = vref.current;
    if (!open || !v) return;
    v.muted = false;
    v.play()?.catch(() => { v.muted = true; v.play()?.catch(() => {}); });
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div key="demo" data-testid="demo-video-modal" role="dialog" aria-modal="true" aria-label="HonestChoice AI explainer video"
          className="fixed inset-0 z-[95] flex items-center justify-center bg-[#030a1f]/80 p-3 backdrop-blur-xl sm:p-8"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}
          onClick={close} data-lenis-prevent>
          <motion.div className="relative w-full max-w-[1100px]" initial={{ scale: 0.94, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 10 }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}>
            <button onClick={close} data-testid="demo-video-close-btn" aria-label="Close video"
              className="absolute -top-12 right-0 grid h-10 w-10 place-items-center rounded-full border border-cyan-300/60 bg-ink/80 text-white shadow-[0_0_16px_rgba(34,211,238,0.4)] transition-colors hover:bg-cyan-400 hover:text-ink sm:-right-12 sm:top-0">
              <X className="h-5 w-5" />
            </button>
            <div className={`relative w-full overflow-hidden rounded-2xl border border-cyan-400/60 bg-black shadow-[0_0_0_1px_rgba(59,130,246,0.25),0_0_40px_rgba(34,211,238,0.35)] ${ended ? "min-h-[360px] sm:aspect-video sm:min-h-0" : "aspect-video"}`}>
              <video ref={vref} data-testid="demo-video-player" src={SRC} controls autoPlay playsInline preload="metadata" onEnded={() => setEnded(true)} onPlay={() => setEnded(false)} className="h-full w-full bg-black object-contain" />
              <AnimatePresence>
                {ended && (
                  <motion.div key="end" data-testid="demo-end-prompt" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}
                    className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#030a1f]/85 px-4 text-center backdrop-blur-md sm:gap-5">
                    <motion.img src={M("logo_mark_sm.webp")} alt="" className="h-12 sm:h-20" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.1 }} />
                    <motion.h3 className="font-display text-2xl font-bold leading-tight text-white sm:text-5xl" initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.7, delay: 0.2 }}>
                      Ready to welcome <span className="text-grad">Honesti</span> home?
                    </motion.h3>
                    <motion.p className="hidden max-w-md text-sm text-slate-200 sm:block sm:text-base" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}>
                      Join the waitlist right here for early updates and first access when she launches.
                    </motion.p>
                    <motion.div className="flex w-full justify-center" initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6, delay: 0.45 }}>
                      <QuickJoin onAgain={again} onFullForm={close} />
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
